'use strict';

const LANGUAGE_MAP = {
    // Non GB-English Speaking Countries
    uk: 'en-gb',
    au: 'en',
    ca: 'en',
    ie: 'en',
    nz: 'en',
    us: 'en-us',
    // Non English Speaking Countries
    at: 'de-at',
    be: 'nl-be',
    br: 'pt-br',
    cn: 'zh-cn',
    cz: 'cs-cz',
    dk: 'da-dk',
    de: 'de-de',
    es: 'es-es',
    fi: 'fi-fi',
    fr: 'fr-fr',
    hk: 'zh-hk',
    hr: 'hr-hr',
    hu: 'hu-hu',
    it: 'it-it',
    mx: 'es-mx',
    jp: 'ja-jp',
    nl: 'nl-nl',
    no: 'no-no',
    pl: 'pl-pl',
    pt: 'pt-pt',
    ro: 'ro-ro',
    ru: 'ru-ru',
    sk: 'sk-sk',
    si: 'sl-si',
    se: 'sv-se'
};
const DEFAULT_LANGUAGE = LANGUAGE_MAP['uk'];
/**
 * A list of valid country codes for use in legacy connect language overrides
 */
const VALID_LEGACY_COUNTRY_CODES = new Set(Object.keys(LANGUAGE_MAP));
const VALID_LANGUAGE_CODES = new Set(Object.values(LANGUAGE_MAP));

/**
 * A function to get the default language code for any given country code
 *
 * @param countryCode - a 2 letter country code e.g. `uk`
 * @returns a HTML language code e.g. `en-gb`
 */
const getLanguageCodeFromCountryCode = (countryCode) => {
    const lowercasedCode = countryCode.toLowerCase();
    const languageCode = LANGUAGE_MAP[lowercasedCode] || DEFAULT_LANGUAGE;
    return languageCode;
};
/**
 * A function to check if a country code has a corresponding language code
 * Legacy connect used country codes to override language, so this function checks that a legacy language code is supported
 *
 * @param countryCode - a 2 letter country code e.g. `uk`
 * @returns a boolean indicating if the country code passed in has an associated language code
 */
const isCountryCodeSupported = (countryCode) => {
    return VALID_LEGACY_COUNTRY_CODES.has(countryCode.toLowerCase());
};
/**
 * A function to check if a language code is supported
 * Brands can use language codes to override language in the new version of connect, so we need to check what they supply is supported before trying to use it
 *
 * @param languageCode - a HTML language code e.g. `en-gb`
 * @returns a boolean indicating if the language code passed in is one we support
 */
const isLanguageCodeSupported = (languageCode) => {
    return VALID_LANGUAGE_CODES.has(languageCode.toLowerCase());
};
/**
 * A function to determine which language code should be used for legacy connects
 *
 * Legacy connect used country codes with no language code in the URL and also used country codes to override language.
 * As such, a legacy connect will have a country code provided, but could have another country code provided to override language.
 * If only a country code is provided, we use that to get the HTML language code to use in the URL for new connect
 * If a language code is provided, we use that to get the HTML language code instead so the product displays in the language requested by a brand
 *
 * @param countryCode - a 2 letter country code indicating which country the connect is for e.g. `uk`
 * @param overrideCountryCode - a 2 letter country code indicating what language to display the product in e.g. `uk`
 * @returns a HTML language code e.g. `en-gb`
 */
const getLegacyOverrideLanguage = (countryCode, overrideCountryCode) => {
    const codeToUse = overrideCountryCode || countryCode;
    return getLanguageCodeFromCountryCode(codeToUse);
};

const LOGGED_OUT_USER_DATA = {
    logged_in: false,
    is_verified: null,
    user_verified_ccgs: null
};
var CCGS;
(function (CCGS) {
    CCGS["STUDENT"] = "student";
    CCGS["GRADUATE"] = "graduate";
    CCGS["HEALTHCARE"] = "healthcare";
    CCGS["TEACHER"] = "teacher";
    CCGS["MILITARY"] = "military";
    CCGS["LOW_INCOME"] = "low-income";
    CCGS["FIRST_RESPONDER"] = "first-responder";
    CCGS["CHARITY_WORKER"] = "charity-worker";
    CCGS["DISABILITY"] = "disability";
    CCGS["PARENT"] = "parent";
})(CCGS || (CCGS = {}));
var VerificationStatus;
(function (VerificationStatus) {
    VerificationStatus["Active"] = "active";
    VerificationStatus["Expired"] = "expired";
    VerificationStatus["Pending"] = "pending";
})(VerificationStatus || (VerificationStatus = {}));

/**
 * Filters a list of active verifications from a user profile object.
 *
 * @template V - The specific type of the verification object, which must extend BaseVerification.
 * @template P - The specific type of the user profile, which must extend BaseUserProfile<V>.
 *
 * @param userProfile The user profile object containing the verifications array.
 *
 * @returns An array containing only the verification objects that are active. The original objects are returned, preserving their full type.
 */
const getActiveVerifications = (userProfile) => {
    const { verifications } = userProfile;
    if (!(verifications === null || verifications === void 0 ? void 0 : verifications.length)) {
        return [];
    }
    return verifications.filter((verification) => verification.status === VerificationStatus.Active);
};
/**
 * Gets the consumer groups from all verifications that match are active.
 *
 * @template V - The specific type of the verification object, which must extend BaseVerification.
 * @template P - The specific type of the user profile, which must extend BaseUserProfile<V>.
 *
 * @param userProfile The user profile object containing the verifications array.
 *
 * @returns An array of TConsumerGroups. Returns an empty array if no matches are found.
 */
const getUserVerifiedCCGs = (userProfile) => {
    const activeVerifications = getActiveVerifications(userProfile);
    return activeVerifications
        .map(({ consumerGroup }) => consumerGroup)
        .filter(isConsumerGroup);
};
/**
 * A type guard that checks if a given value is a valid ConsumerGroups string.
 * This function allows us to safely filter and cast arrays of strings.
 * @param value The value to check.
 * @returns True if the value is a valid ConsumerGroups member.
 */
function isConsumerGroup(value) {
    const validConsumerGroups = new Set(Object.values(CCGS));
    return typeof value === 'string' && validConsumerGroups.has(value);
}

var APPLICATION_IDS;
(function (APPLICATION_IDS) {
    APPLICATION_IDS["studentbeans"] = "studentbeans";
    APPLICATION_IDS["gradbeans"] = "gradbeans";
    APPLICATION_IDS["beansidConnect"] = "connect";
    APPLICATION_IDS["beansidMarketplace"] = "beans_id_marketplace";
    APPLICATION_IDS["browser_extension"] = "browser_extension";
    APPLICATION_IDS["accounts"] = "accounts";
    APPLICATION_IDS["ios"] = "ios";
    APPLICATION_IDS["android"] = "android";
    APPLICATION_IDS["unknown"] = "unknown";
})(APPLICATION_IDS || (APPLICATION_IDS = {}));
const CONNECT_APP_ID_TYPES = {
    hosted: 'hosted',
    embedded: 'embedded',
    'hosted-app': 'hosted-app',
    'embedded-app': 'embedded-app'
};
const WEBSITE_APP_ID_TYPE = 'website';
var PAGE_TYPES;
(function (PAGE_TYPES) {
    PAGE_TYPES["ABOUT_PAGE"] = "about_page";
    PAGE_TYPES["ACCESSIBILITY_PAGE"] = "accessibility_page";
    PAGE_TYPES["ALL_DISCOUNTS_PAGE"] = "all_discounts_page";
    PAGE_TYPES["BRAND_PAGE"] = "brand_page";
    PAGE_TYPES["BRAND_LIST_PAGE"] = "brand_list_page";
    PAGE_TYPES["CAMPAIGN_PAGE"] = "campaign_page";
    PAGE_TYPES["CAMPAIGN_LIST_PAGE"] = "campaign_list_page";
    PAGE_TYPES["CATEGORY_PAGE"] = "category_page";
    PAGE_TYPES["CATEGORY_LIST_PAGE"] = "category_list_page";
    PAGE_TYPES["CCG_PAGE"] = "ccg_page";
    PAGE_TYPES["CCG_LIST_PAGE"] = "ccg_list_page";
    PAGE_TYPES["COLLECTION_PAGE"] = "collection_page";
    PAGE_TYPES["COLLECTION_LIST_PAGE"] = "collection_list_page";
    PAGE_TYPES["COUNTRY_SELECTION_PAGE"] = "country_selection_page";
    PAGE_TYPES["ERROR_PAGE"] = "error_page";
    PAGE_TYPES["HOME_PAGE"] = "home_page";
    PAGE_TYPES["IMPRESSUM_PAGE"] = "impressum_page";
    PAGE_TYPES["OFFER_LIST_PAGE"] = "offer_list_page";
    PAGE_TYPES["OFFER_PAGE"] = "offer_page";
    PAGE_TYPES["OTHER"] = "other";
    PAGE_TYPES["RECOMMENDED_PAGE"] = "recommended_page";
    PAGE_TYPES["TERMS_AND_CONDITIONS_PAGE"] = "terms_and_conditions_page";
    PAGE_TYPES["TRENDING_PAGE"] = "trending_page";
    PAGE_TYPES["INSTORE_OFFERS_PAGE"] = "instore_offers_page";
    PAGE_TYPES["UNSUBSCRIBE_PAGE"] = "unsubscribe_page";
})(PAGE_TYPES || (PAGE_TYPES = {}));

const SB_STANDARD_CONTEXT_SCHEMA = 'iglu:com.studentbeans/sb_standard_context/jsonschema/2-1-0';

const getStandardContextUserData = (userProfile) => {
    if (!userProfile.isLoggedIn)
        return LOGGED_OUT_USER_DATA;
    const userVerifiedCCGs = getUserVerifiedCCGs(userProfile);
    return {
        logged_in: true,
        is_verified: !!userProfile.verified,
        user_verified_ccgs: userVerifiedCCGs
    };
};
const getConnectAppIdType = (connectImplementation) => {
    if (!connectImplementation)
        return CONNECT_APP_ID_TYPES.hosted;
    return CONNECT_APP_ID_TYPES[connectImplementation];
};
const VALID_APP_IDS = new Set(Object.values(APPLICATION_IDS));
const isValidAppId = (appId) => {
    return VALID_APP_IDS.has(appId);
};
const getAccountsAppId = (appIdCookie) => {
    if (appIdCookie && isValidAppId(appIdCookie))
        return appIdCookie;
    return APPLICATION_IDS.unknown;
};
const getStandardContextAppIdType = (application, appIdCookie, connectImplementation) => {
    if (application === APPLICATION_IDS.accounts)
        return getAccountsAppId(appIdCookie);
    if (application === APPLICATION_IDS.beansidConnect) {
        return getConnectAppIdType(connectImplementation);
    }
    return WEBSITE_APP_ID_TYPE;
};
const getStandardContext = (application, pageType, country, userProfile, appIdCookie, connectImplementation) => {
    const appIdType = getStandardContextAppIdType(application, appIdCookie, connectImplementation);
    return {
        schema: SB_STANDARD_CONTEXT_SCHEMA,
        data: Object.assign({ app_id_type: appIdType, page_type: pageType, country_code: country }, getStandardContextUserData(userProfile))
    };
};
/**
 * Creates an array of global context objects, starting with the standard context.
 *
 * @template V - The specific type of the verification object, which must extend BaseVerification.
 * @template P - The specific type of the user profile, which must extend BaseUserProfile<V>.
 *
 * @param application The current top-level application, use APPLICATION_IDS from js_shared_utils.
 * @param pageType The type of the current page, please use PAGE_TYPE from js_shared_utils.
 * @param country The country code.
 * @param userProfile The user's profile object.
 * @param appIdCookie The application ID from the user's cookie.
 * @param connectConnectImplementation The specific connect implementation, if any.
 *
 * @returns An array containing the StandardContext.
 */
const getGlobalContexts = (application, pageType, country, userProfile, appIdCookie, connectConnectImplementation) => {
    return [
        getStandardContext(application, pageType, country, userProfile, appIdCookie, connectConnectImplementation)
    ];
};

exports.getActiveVerifications = getActiveVerifications;
exports.getGlobalContexts = getGlobalContexts;
exports.getLanguageCodeFromCountryCode = getLanguageCodeFromCountryCode;
exports.getLegacyOverrideLanguage = getLegacyOverrideLanguage;
exports.getUserVerifiedCCGs = getUserVerifiedCCGs;
exports.isConsumerGroup = isConsumerGroup;
exports.isCountryCodeSupported = isCountryCodeSupported;
exports.isLanguageCodeSupported = isLanguageCodeSupported;
